import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { MapPin, Truck } from "lucide-react";
import { updateDriver } from "@/lib/api";

const inputCls =
  "h-8 text-sm bg-[#0F172A] border-[#2A364F] focus-visible:ring-blue-500 text-slate-100";

function Field({ label, children }) {
  return (
    <div className="space-y-0.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function splitVan(v) {
  const parts = (v || "").split(" - ");
  return { vehicle: parts[0] || "", plate: parts.slice(1).join(" - ") };
}

export default function DriverCard({ driver }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(driver);
  const [vehicle, setVehicle] = useState(splitVan(driver.van_details).vehicle);
  const [plate, setPlate] = useState(splitVan(driver.van_details).plate);

  useEffect(() => {
    setForm(driver);
    const s = splitVan(driver.van_details);
    setVehicle(s.vehicle);
    setPlate(s.plate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver.id, driver.updated_at]);

  const mut = useMutation({
    mutationFn: (data) => updateDriver(driver.id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drivers"] }),
  });

  const save = (patch) => mut.mutate(patch);
  const onBlurSave = (key) => {
    if (form[key] !== driver[key]) save({ [key]: form[key] });
  };
  const saveVan = () => {
    const vd = [vehicle.trim(), plate.trim()].filter(Boolean).join(" - ");
    if (vd !== (driver.van_details || "")) save({ van_details: vd });
  };

  const isFree = form.status === "free";
  const tid = driver.label.replace(/\s+/g, "-");

  return (
    <div
      data-testid={`driver-card-${tid}`}
      className={
        "rounded-xl border p-3 space-y-2 transition-colors " +
        (isFree ? "bg-[#151C28] border-green-800/60" : "bg-[#141824] border-[#2A364F]")
      }
    >
      {/* Header row */}
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={
            "shrink-0 w-7 h-7 rounded-md flex items-center justify-center font-display font-extrabold text-sm " +
            (isFree ? "bg-green-600 text-white" : "bg-orange-600 text-white")
          }
        >
          {driver.label.match(/\d+/)?.[0] || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-bold leading-tight">{driver.label}</div>
          <div className="text-[11px] text-slate-400 truncate flex items-center gap-1">
            <Truck className="w-3 h-3" /> {[vehicle, plate].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <Switch
          data-testid={`driver-status-switch-${tid}`}
          checked={isFree}
          onCheckedChange={(v) => {
            const status = v ? "free" : "busy";
            setForm((f) => ({ ...f, status }));
            save({ status });
          }}
          className="scale-110 data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-orange-600"
        />
      </div>

      {/* Status label */}
      <div
        className={
          "text-center rounded-lg py-1 font-display font-extrabold text-xs " +
          (isFree
            ? "bg-green-950/40 text-green-400 border border-green-700"
            : "bg-orange-950/40 text-orange-400 border border-orange-700")
        }
      >
        {isFree ? "LIBER / CAUTĂ CURSĂ" : "OCUPAT / ÎN CURSĂ"}
      </div>

      <Field label="Nume Șofer">
        <Input
          className={inputCls}
          value={form.name || ""}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          onBlur={() => onBlurSave("name")}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Mașină">
          <Input
            data-testid={`driver-vehicle-input-${tid}`}
            className={inputCls}
            value={vehicle}
            onChange={(e) => setVehicle(e.target.value)}
            onBlur={saveVan}
            placeholder="Ex: Mercedes Sprinter"
          />
        </Field>
        <Field label="Nr. Înmatriculare">
          <Input
            data-testid={`driver-plate-input-${tid}`}
            className={inputCls + " font-mono"}
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            onBlur={saveVan}
            placeholder="Ex: B 101 ABC"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Oraș">
          <Input
            className={inputCls}
            value={form.city || ""}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            onBlur={() => onBlurSave("city")}
          />
        </Field>
        <Field label="Țară">
          <Input
            className={inputCls}
            value={form.country || ""}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            onBlur={() => onBlurSave("country")}
          />
        </Field>
      </div>

      <Field label="Cod Poștal (locația actuală)">
        <div className="relative">
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
          <Input
            data-testid={`driver-postal-input-${tid}`}
            className={inputCls + " pl-8 font-mono font-bold tracking-wide"}
            value={form.postal_code || ""}
            onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))}
            onBlur={() => onBlurSave("postal_code")}
          />
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Capacitate (paleți)">
          <Input
            type="number"
            min="0"
            data-testid={`driver-pallets-input-${tid}`}
            className={inputCls + " font-mono font-bold"}
            value={form.pallets_capacity ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, pallets_capacity: e.target.value }))}
            onBlur={() => {
              const n = parseInt(form.pallets_capacity, 10);
              if (!Number.isNaN(n) && n !== driver.pallets_capacity) save({ pallets_capacity: n });
            }}
          />
        </Field>
        <Field label="Capacitate (kg)">
          <Input
            type="number"
            min="0"
            data-testid={`driver-weight-input-${tid}`}
            className={inputCls + " font-mono font-bold"}
            value={form.weight_capacity ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, weight_capacity: e.target.value }))}
            onBlur={() => {
              const n = parseInt(form.weight_capacity, 10);
              if (!Number.isNaN(n) && n !== driver.weight_capacity) save({ weight_capacity: n });
            }}
          />
        </Field>
      </div>
    </div>
  );
}
